import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, PlayCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { ragApi } from '@/api/rag';
import { unwrapError } from '@/api/client';
import {
  INGESTION_TERMINAL_STATUSES,
  type IngestionJobSummary,
} from '@/api/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';

const statusVariant = (s: string): 'success' | 'default' | 'destructive' | 'warning' | 'secondary' => {
  if (s === 'COMPLETE') return 'success';
  if (s === 'FAILED') return 'destructive';
  if (s === 'STOPPED') return 'warning';
  if (s === 'STARTING' || s === 'IN_PROGRESS') return 'default';
  return 'secondary';
};

export function IngestionCard(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [description, setDescription] = React.useState('');

  const query = useQuery({
    queryKey: ['rag', 'jobs'],
    queryFn: () => ragApi.listIngestionJobs({ maxResults: 25 }),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      return data.jobs.some((j) => !INGESTION_TERMINAL_STATUSES.has(j.status)) ? 5000 : false;
    },
  });

  const trigger = useMutation({
    mutationFn: () => ragApi.startIngestionJob(description.trim() || undefined),
    onSuccess: (job) => {
      toast.success(t('rag.triggeredToast', { id: job.ingestionJobId.slice(0, 8), status: job.status }));
      setDescription('');
      void queryClient.invalidateQueries({ queryKey: ['rag', 'jobs'] });
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const jobs: IngestionJobSummary[] = query.data?.jobs ?? [];
  const hasPending = jobs.some((j) => !INGESTION_TERMINAL_STATUSES.has(j.status));

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base">
          {t('rag.ingestionTitle')}
          {hasPending ? (
            <Badge variant="default" className="ml-2 gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('rag.inProgress')}
            </Badge>
          ) : null}
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
          className="gap-2"
        >
          <RefreshCw className={query.isFetching ? 'h-3 w-3 animate-spin' : 'h-3 w-3'} />
          {t('common.refresh')}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4 p-4 pt-0">
        <div className="flex flex-wrap items-end gap-2 rounded-md border bg-muted/30 p-3">
          <div className="flex-1 min-w-[200px] space-y-1">
            <Label htmlFor="desc" className="text-xs">
              {t('rag.ingestionDescription')}
            </Label>
            <Input
              id="desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={200}
              placeholder={t('rag.ingestionPlaceholder')}
            />
          </div>
          <Button
            onClick={() => trigger.mutate()}
            disabled={trigger.isPending}
            className="gap-2"
          >
            {trigger.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PlayCircle className="h-4 w-4" />
            )}
            {t('rag.trigger')}
          </Button>
        </div>

        {query.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('common.loading')}
          </div>
        ) : query.error ? (
          <p className="text-sm text-destructive">{unwrapError(query.error)}</p>
        ) : jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('rag.noJobs')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('rag.ingestionTable.job')}</TableHead>
                <TableHead>{t('rag.ingestionTable.status')}</TableHead>
                <TableHead>{t('rag.ingestionTable.started')}</TableHead>
                <TableHead>{t('rag.ingestionTable.updated')}</TableHead>
                <TableHead className="text-right">{t('rag.ingestionTable.indexed')}</TableHead>
                <TableHead className="text-right">{t('rag.ingestionTable.failed')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((j) => (
                <React.Fragment key={j.ingestionJobId}>
                  <TableRow>
                    <TableCell>
                      <code className="font-mono text-[10px]">{j.ingestionJobId}</code>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Badge variant={statusVariant(j.status)}>{j.status}</Badge>
                        {j.status === 'COMPLETE' ? (
                          <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {j.startedAt ? formatDate(j.startedAt) : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {j.updatedAt ? formatDate(j.updatedAt) : '—'}
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {(j.statistics?.newDocumentsIndexed ?? 0) +
                        (j.statistics?.modifiedDocumentsIndexed ?? 0)}
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {j.statistics?.documentsFailed ?? 0}
                    </TableCell>
                  </TableRow>
                  {j.failureReasons && j.failureReasons.length > 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="border-t-0 py-1 pl-4">
                        <Alert variant="destructive" className="py-2 text-xs">
                          <AlertTriangle className="h-3 w-3" />
                          <AlertDescription className="text-xs">
                            {j.failureReasons.join(' ; ')}
                          </AlertDescription>
                        </Alert>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
